import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, Users, MessageSquare, CreditCard, History, Building } from 'lucide-react';
import Button from '../common/Button';
import Loading from '../common/Loading';
import { userAPI } from '../../api/endpoints';
import { useToastContext } from '../../context/ToastContext';

import { decodeHtml } from '../../utils/formatters';

const OrganizationStatsModal = ({ isOpen, onClose, organization }: { isOpen: boolean, onClose: () => void, organization: any }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const toast = useToastContext();

  const { data: statsData, isLoading, error } = useQuery({
    queryKey: ['organizationStats', organization?.id],
    queryFn: async () => {
      const response = await userAPI.getOrganizationStats(organization.id);
      return response.data;
    },
    enabled: isOpen && !!organization?.id,
    retry: false,
  });

  React.useEffect(() => {
    if (error) {
      toast.error((error as any).response?.data?.message || 'Failed to load organization stats');
    }
  }, [error, toast]);

  if (!isOpen || !organization) return null;

  const { stats, payment_history, sms_usage } = (statsData as any) || {};

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative transform overflow-hidden rounded-xl bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-teal-100 p-2 rounded-lg text-teal-600">
                <Building className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 leading-tight">
                  {decodeHtml(organization.organization_name) || organization.full_name}
                </h3>
                <p className="text-sm text-gray-500">{organization.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="border-b border-gray-200 px-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'overview'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'payments'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payment History
              </button>
              <button
                onClick={() => setActiveTab('sms')}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'sms'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                SMS Usage
              </button>
            </nav>
          </div>

          {/* Body */}
          <div className="px-6 py-6 bg-white min-h-[400px]">
            {isLoading ? (
              <div className="flex justify-center items-center h-64">
                <Loading />
              </div>
            ) : error ? (
              <div className="text-center py-12 text-gray-500">
                Failed to load data.
              </div>
            ) : (
              <>
                {/* OVERVIEW TAB */}
                {activeTab === 'overview' && stats && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex items-start gap-4">
                      <div className="bg-blue-100 p-3 rounded-xl text-blue-600">
                        <Users className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Users</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.user_count}</p>
                        <p className="text-xs text-gray-400 mt-1">Users created by this organization</p>
                      </div>
                    </div>
                    
                    <div className="bg-gray-50 rounded-2xl p-6 border border-gray-100 flex items-start gap-4">
                      <div className="bg-emerald-100 p-3 rounded-xl text-emerald-600">
                        <MessageSquare className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">SMS Balance</p>
                        <p className="text-3xl font-bold text-gray-900 mt-1">{stats.sms_balance}</p>
                        <p className="text-xs text-gray-400 mt-1">Available credit units</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* PAYMENTS TAB */}
                {activeTab === 'payments' && (
                  <div>
                    {!payment_history || payment_history.length === 0 ? (
                      <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 text-sm bg-gray-50/50">
                        <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        No payment history found.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm bg-white">
                        <table className="w-full text-sm text-left text-gray-600 divide-y divide-gray-200">
                          <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 font-semibold tracking-wider">
                            <tr>
                              <th className="px-6 py-4 whitespace-nowrap">Date</th>
                              <th className="px-6 py-4 whitespace-nowrap">Reference</th>
                              <th className="px-6 py-4 whitespace-nowrap text-right">Amount</th>
                              <th className="px-6 py-4 whitespace-nowrap text-right">Credits</th>
                              <th className="px-6 py-4 whitespace-nowrap text-center">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {payment_history.map((pay: any) => (
                              <tr key={pay.id} className="hover:bg-gray-50/80 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                  {new Date(pay.created_at).toLocaleString(undefined, {
                                    year: 'numeric', month: 'short', day: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap font-mono text-xs text-gray-500">
                                  {pay.paystack_reference}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                                  GH₵{parseFloat(pay.amount).toLocaleString()}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-teal-600">
                                  +{pay.credit_units}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                  <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold tracking-wide ${
                                    pay.status === 'verified'
                                      ? 'bg-green-100 text-green-700'
                                      : pay.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-red-100 text-red-700'
                                  }`}>
                                    {pay.status.charAt(0).toUpperCase() + pay.status.slice(1)}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}

                {/* SMS USAGE TAB */}
                {activeTab === 'sms' && (
                  <div>
                    {!sms_usage || sms_usage.length === 0 ? (
                      <div className="text-center py-16 border-2 border-dashed border-gray-200 rounded-2xl text-gray-500 text-sm bg-gray-50/50">
                        <History className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        No SMS usage found.
                      </div>
                    ) : (
                      <div className="overflow-x-auto border border-gray-200 rounded-2xl shadow-sm bg-white">
                        <table className="w-full text-sm text-left text-gray-600 divide-y divide-gray-200">
                          <thead className="text-xs text-gray-500 uppercase bg-gray-50/80 font-semibold tracking-wider">
                            <tr>
                              <th className="px-6 py-4 whitespace-nowrap">Date</th>
                              <th className="px-6 py-4 whitespace-nowrap text-center">Recipients</th>
                              <th className="px-6 py-4 whitespace-nowrap text-center">Success</th>
                              <th className="px-6 py-4 whitespace-nowrap text-center">Failed</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 bg-white">
                            {sms_usage.map((log: any) => (
                              <tr key={log.id} className="hover:bg-gray-50/80 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                                  {new Date(log.created_at).toLocaleString(undefined, {
                                    year: 'numeric', month: 'short', day: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                  })}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-gray-700">
                                  {log.recipient_count}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-green-600">
                                  {log.success_count}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center font-bold text-red-600">
                                  {log.failure_count}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
            <Button onClick={onClose} variant="secondary">
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationStatsModal;
